import React from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { useHistory, useLocation, useParams } from 'react-router-dom/cjs/react-router-dom.min';
import { useRouteConfiguration } from '../../context/routeConfigurationContext';
import { connect, useDispatch, useSelector } from 'react-redux';
import { isScrollingDisabled } from '../../ducks/ui.duck';
import { H1, H3, LayoutSingleColumn, Page } from '../../components';
import TopbarContainer from '../TopbarContainer/TopbarContainer';
import FooterContainer from '../FooterContainer/FooterContainer';
import css from './ServiceOfferingsPage.module.css';
import { getMarketplaceEntities } from '../../ducks/marketplaceData.duck';
import { compose } from 'redux';
import { types as sdkTypes } from '../../util/sdkLoader';
import { ensureListing } from '../../util/data';
import { draftId } from '../../routing/routeConfiguration';
import SectionOfferingsForm from './SectionOfferingsForm/SectionOfferingsForm';
import { getUnlockableContent } from '../../util/listingDataExtraction';
import { extractAllPhotosFromUnlockableContent } from '../../util/dataExtractor';
import { useConfiguration } from '../../context/configurationContext';
import { getDisplayName } from '../../util/userDataExtraction';
import { userSelector } from '../../ducks/user.duck';
import { initializeCardPaymentData } from '../../ducks/stripe.duck';
import { createSlug } from '../../util/urlHelpers';
const { UUID, Money } = sdkTypes;
import { handleSubmit as handleCheckoutSubmit } from '../ListingPage/ListingPage.shared';
import { DeliveryMethod, TRANSACTION_PROCESS_ALIAS, UNIT_TYPE } from '../../util/enums';
import { isArrayLength } from '../../util/genericHelpers';
import { showErrorToast } from '../../util/toast';
import { getListingDeliveryType } from '../../util/helperFunction';

const ServiceOfferingsPage = ({ getListing }) => {
  const intl = useIntl();
  const dispatch = useDispatch();
  const history = useHistory();
  const routeConfiguration = useRouteConfiguration();
  const config = useConfiguration();
  const marketplaceCurrency = config.currency;
  const location = useLocation();
  const scrollingDisabled = useSelector(isScrollingDisabled);
  const currentUser = useSelector(userSelector);
  const schemaTitle = intl.formatMessage({
    id: 'ServiceOfferingsPage.schemaTitle',
  });
  const { id = draftId, type } = useParams(); // Correctly get id from URL
  // Safely get the listing using the helper from props
  const rawListing = id ? getListing(new UUID(id)) : null;
  const currentListing = ensureListing(rawListing);
  const currentAuthor = currentListing?.author;
  const authorDisplayName = getDisplayName(currentAuthor);
  const currentItemForPurchase = location.state?.item;
  const platform = currentItemForPurchase?.platform;
  const { title, description } = currentListing?.attributes || {};
  const unlockableContent = getUnlockableContent(currentListing);
  // Extract ALL photos (from photos[] and posts[].photo)
  const allUnlockablePhotos = extractAllPhotosFromUnlockableContent(unlockableContent);

  const initialValues = {
    title: currentItemForPurchase?.title,
    price: new Money(currentItemForPurchase?.price * 100, marketplaceCurrency),
    ...(platform && { platform }),
  };

  const handleSubmit = async values => {
    try {
      const { selectedPhotos: selectedPhotoIds = [] } = values || {};

      // === NEW: Map selected IDs back to full photo objects ===
      const selectedPhotosFull = selectedPhotoIds
        .map(id => allUnlockablePhotos.find(photo => photo.id === id))
        .filter(Boolean); // remove any not found (safety)

      // Replace selectedPhotos with full objects
      const updatedValues = {
        ...values,
        ...(isArrayLength(selectedPhotosFull) && {
          selectedPhotos: selectedPhotosFull,
        }),
      };

      const listingParams = {
        id: currentListing.id.uuid,
        slug: createSlug(title), // assuming title comes from values or elsewhere
      };

      const listingDeliveryType = getListingDeliveryType(type, currentItemForPurchase?.title);
      // Now use updatedValues with full objects
      const serviceOfferParams = {
        ...updatedValues,
        serviceType: type,
        listingDeliveryType,
        unitType: UNIT_TYPE.ITEM,
        processAlias: TRANSACTION_PROCESS_ALIAS.DEFAULT_PURCHASE,
      };
      const checkoutParams = {
        params: listingParams,
        ...updatedValues, // full objects here
        currentUser,
        history,
        routes: routeConfiguration,
        getListing,
        callSetInitialValues: (setInitialValues, values, saveToSessionStorage) =>
          dispatch(setInitialValues(values, saveToSessionStorage)),
        onInitializeCardPaymentData: () => dispatch(initializeCardPaymentData()),
        serviceOfferParams,
      };

      await handleCheckoutSubmit(checkoutParams)({
        quantity: 1,
        deliveryMethod: DeliveryMethod.SHIPPING,
      });
    } catch (error) {
      showErrorToast('Error while creating checkout');
    }
  };
  return (
    <Page title={schemaTitle} scrollingDisabled={scrollingDisabled}>
      <LayoutSingleColumn topbar={<TopbarContainer />} footer={<FooterContainer />}>
        <div className={css.mainContent}>
          <H1 as="h1" className={css.title}>
            <FormattedMessage id="ServiceOfferingsPage.title" />
          </H1>
          <SectionOfferingsForm
            initialValues={initialValues}
            onSubmit={handleSubmit}
            marketplaceCurrency={marketplaceCurrency}
            photoOptions={allUnlockablePhotos}
            type={type}
            platform={platform}
            listing={currentListing}
          />
        </div>
      </LayoutSingleColumn>
    </Page>
  );
};
// Properly map state to props — only define getListing once here
const mapStateToProps = state => {
  const getListing = id => {
    if (!id) return null;
    const ref = { id, type: 'listing' };
    const listings = getMarketplaceEntities(state, [ref]);
    return listings.length === 1 ? listings[0] : null;
  };

  return {
    getListing,
  };
};

export default compose(connect(mapStateToProps))(ServiceOfferingsPage);
